import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Badge } from '@/components/ui'
import { Globe, Copy, Check, RefreshCw, Loader2, Search, MapPin, Building, Clock, Phone, DollarSign, Network, Server, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

interface RateLimit {
  remaining: number
  limit: number
  used: number
}

interface IpData {
  ip: string
  type: string
  continent: string
  continent_code: string
  country: string
  country_code: string
  country_flag: string
  country_capital: string
  country_phone: string
  country_neighbours: string
  region: string
  city: string
  latitude: number
  longitude: number
  asn: string
  org: string
  isp: string
  timezone: string
  timezone_name: string
  timezone_gmt: string
  currency: string
  currency_code: string
  currency_symbol: string
  currency_rates: number
  currency_plural: string
}

export function IpInfo() {
  const [myIp, setMyIp] = useState<string | null>(null)
  const [myIpData, setMyIpData] = useState<IpData | null>(null)
  const [lookupIp, setLookupIp] = useState('')
  const [lookupData, setLookupData] = useState<IpData | null>(null)
  const [loading, setLoading] = useState(false)
  const [lookupLoading, setLookupLoading] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null)

  const fetchMyIp = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/tools/ip-lookup', { credentials: 'include' })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.error || 'Failed to fetch IP information')
        if (data.remaining !== undefined) setRateLimit({ remaining: data.remaining, limit: data.limit, used: 0 })
        return
      }
      if (data._rateLimit) {
        setRateLimit(data._rateLimit)
        delete data._rateLimit
      }
      setMyIp(data.ip)
      setMyIpData(data)
    } catch {
      toast.error('Failed to fetch IP information')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMyIp()
  }, [])

  const lookupIpAddress = async () => {
    const ip = lookupIp.trim()
    if (!ip) {
      toast.error('Enter an IP address')
      return
    }
    setLookupLoading(true)
    setLookupData(null)
    try {
      const res = await fetch(`/api/tools/ip-lookup?ip=${encodeURIComponent(ip)}`, { credentials: 'include' })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.error || 'Invalid IP address')
        if (data.remaining !== undefined) setRateLimit({ remaining: data.remaining, limit: data.limit, used: 0 })
        return
      }
      if (data._rateLimit) {
        setRateLimit(data._rateLimit)
        delete data._rateLimit
      }
      setLookupData(data)
    } catch {
      toast.error('Failed to lookup IP address')
    } finally {
      setLookupLoading(false)
    }
  }

  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text)
    setCopied(key)
    setTimeout(() => setCopied(null), 2000)
  }

  const InfoRow = ({ label, value, icon: Icon, copyable }: { label: string; value: string | number | boolean | undefined; icon?: React.ComponentType<{ className?: string }>; copyable?: boolean }) => {
    if (value === undefined || value === null || value === '') return null
    const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)
    const key = `${label}-${display}`
    return (
      <div className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-mono">{display}</span>
          {copyable && (
            <button onClick={() => copyText(display, key)} className="text-muted-foreground hover:text-foreground transition-colors">
              {copied === key ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
            </button>
          )}
        </div>
      </div>
    )
  }

  const IpDetails = ({ data, title }: { data: IpData; title: string }) => (
    <div className="space-y-4">
      <Card className="glass">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Network</CardTitle>
            <Badge variant="secondary">{data.type}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <InfoRow label="IP Address" value={data.ip} icon={Globe} copyable />
          <InfoRow label="ASN" value={data.asn} icon={Server} copyable />
          <InfoRow label="ISP" value={data.isp} icon={Network} />
          <InfoRow label="Organization" value={data.org} icon={Building} />
        </CardContent>
      </Card>

      <Card className="glass">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Location</CardTitle>
        </CardHeader>
        <CardContent>
          <InfoRow label="City" value={data.city} icon={MapPin} />
          <InfoRow label="Region" value={data.region} />
          <InfoRow label="Country" value={`${data.country} (${data.country_code})`} />
          <InfoRow label="Capital" value={data.country_capital} />
          <InfoRow label="Continent" value={data.continent} />
          <InfoRow label="Latitude" value={data.latitude} />
          <InfoRow label="Longitude" value={data.longitude} />
          <InfoRow label="Neighbours" value={data.country_neighbours} />
        </CardContent>
      </Card>

      <Card className="glass">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Country Details</CardTitle>
        </CardHeader>
        <CardContent>
          <InfoRow label="Timezone" value={data.timezone} icon={Clock} />
          <InfoRow label="GMT Offset" value={data.timezone_gmt} />
          <InfoRow label="Calling Code" value={data.country_phone} icon={Phone} />
          <InfoRow label="Currency" value={`${data.currency} (${data.currency_code} ${data.currency_symbol})`} icon={DollarSign} />
        </CardContent>
      </Card>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
            <Globe className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">IP Information</h1>
            <p className="text-muted-foreground">View your IP details or look up any IP address</p>
            {rateLimit && (
              <div className="flex items-center gap-1.5 mt-1">
                <AlertCircle className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {rateLimit.remaining} / {rateLimit.limit} lookups remaining today
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* My IP */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Your IP</h2>
            <Button variant="outline" size="sm" onClick={fetchMyIp} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <Card className="glass">
              <CardContent className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </CardContent>
            </Card>
          ) : myIpData ? (
            <IpDetails data={myIpData} title="Your IP" />
          ) : (
            <Card className="glass">
              <CardContent className="py-12 text-center text-muted-foreground">
                Failed to load IP information
              </CardContent>
            </Card>
          )}
        </div>

        {/* IP Lookup */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">IP Lookup</h2>

          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={lookupIp}
                    onChange={e => setLookupIp(e.target.value)}
                    placeholder="Enter IP address (e.g. 8.8.8.8)"
                    className="pl-9"
                    onKeyDown={e => { if (e.key === 'Enter') lookupIpAddress() }}
                    disabled={lookupLoading}
                  />
                </div>
                <Button onClick={lookupIpAddress} disabled={lookupLoading || !lookupIp.trim()}>
                  {lookupLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {lookupLoading ? (
            <Card className="glass">
              <CardContent className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </CardContent>
            </Card>
          ) : lookupData ? (
            <IpDetails data={lookupData} title="Lookup Result" />
          ) : (
            <Card className="glass">
              <CardContent className="py-12 text-center text-muted-foreground">
                <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Enter an IP address to look up its details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
